import React from 'react';
import { Progress } from './ui/progress';
import { TrendingUp, Flame, Zap, Clock } from 'lucide-react';
import { formatNumber, getMomentumColor } from '../lib/utils';

export function MomentumScore({ 
  score, 
  freeScore = 0, 
  timeScore = 0, 
  moneyScore = 0,
  showBreakdown = false,
  size = 'default' 
}) {
  const maxScore = 500; // Display cap
  const normalizedScore = Math.min(score, maxScore);
  const percentage = (normalizedScore / maxScore) * 100;

  const getIntensityLevel = () => {
    if (score >= 200) return { label: 'On Fire', icon: Flame, color: 'text-red-500' };
    if (score >= 100) return { label: 'Hot', icon: TrendingUp, color: 'text-orange-500' };
    if (score >= 50) return { label: 'Rising', icon: Zap, color: 'text-yellow-500' };
    return { label: 'Building', icon: Clock, color: 'text-slate-500' };
  };

  const intensity = getIntensityLevel();
  const IconComponent = intensity.icon;

  if (size === 'compact') {
    return (
      <div className="flex items-center gap-2">
        <IconComponent className={`w-4 h-4 ${intensity.color}`} />
        <span className={`font-bold ${getMomentumColor(score)}`}>
          {formatNumber(Math.round(score))}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Main Score Display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconComponent className={`w-5 h-5 ${intensity.color}`} />
          <span className="text-sm font-medium text-slate-600">Momentum</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-2xl font-bold momentum-value ${getMomentumColor(score)}`}>
            {formatNumber(Math.round(score))}
          </span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            intensity.color === 'text-red-500' ? 'bg-red-100 text-red-700' :
            intensity.color === 'text-orange-500' ? 'bg-orange-100 text-orange-700' :
            intensity.color === 'text-yellow-500' ? 'bg-yellow-100 text-yellow-700' :
            'bg-slate-100 text-slate-700'
          }`}>
            {intensity.label}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="momentum-bar h-full"
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Score Breakdown */}
      {showBreakdown && (
        <div className="grid grid-cols-3 gap-4 pt-2">
          <ScoreBreakdownItem
            label="Free Boosts"
            value={freeScore}
            multiplier="×1"
            color="bg-blue-500"
          />
          <ScoreBreakdownItem
            label="Time Boosts"
            value={timeScore}
            multiplier="×2"
            color="bg-green-500"
          />
          <ScoreBreakdownItem
            label="Money Boosts"
            value={moneyScore}
            multiplier="×3"
            color="bg-orange-500"
          />
        </div>
      )}
    </div>
  );
}

function ScoreBreakdownItem({ label, value, multiplier, color }) {
  return (
    <div className="text-center p-3 bg-slate-50 rounded-xl">
      <div className="flex items-center justify-center gap-1 mb-1">
        <div className={`w-2 h-2 rounded-full ${color}`} />
        <span className="text-xs text-slate-500">{multiplier}</span>
      </div>
      <div className="font-bold text-slate-900">{formatNumber(Math.round(value))}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

export function MomentumIndicator({ score }) {
  const getColor = () => {
    if (score >= 200) return 'from-red-500 to-orange-500';
    if (score >= 100) return 'from-orange-500 to-yellow-500';
    if (score >= 50) return 'from-yellow-500 to-green-500';
    return 'from-slate-400 to-slate-300';
  };

  return (
    <div 
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r ${getColor()} text-white font-bold text-sm shadow-lg`}
      data-testid="momentum-indicator"
    >
      <TrendingUp className="w-4 h-4" />
      {formatNumber(Math.round(score))}
    </div>
  );
}
